import { useState, Fragment } from 'react';
import { Add, UnfoldLess, UnfoldMore } from '@mui/icons-material';
import {
  Theme,
  Box,
  Button,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  FormControlLabel,
  Switch
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Page, Loader, Pagination, PageTitleWithButton } from '../../../components';
import makeStyles from '@mui/styles/makeStyles';
import { useQuery } from 'react-query';
import { IColorSchemesResponse, IResponse } from '../../../models';
import { getColorSchemesList } from '../../../fetch/color-schemes';
import { ManageColorSchemeFilter } from './manage-color-scheme-filter';
import { ColorSchemeAccordion } from './ColorSchemeAccordion';

export const ManageColorSchemes = () => {
  const classes = useStyles();

  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);

  const [showInactive, setShowInactive] = useState(false);
  const [isAccordionsExpanded, setAccordionsExpanded] = useState<boolean>(false);
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [searchValue, setSearch] = useState<string>('');
  const [filters, setFilters] = useState<{
    search?: string;
  } | null>({});
  const { enqueueSnackbar } = useSnackbar();

  const {
    isLoading: isLoadingColorSchemes,
    data: colorSchemes,
    refetch: refetchColorSchemeList
  } = useQuery<IColorSchemesResponse, Error>(
    ['getColorSchemesList', filters.search, page, perPage, showInactive],
    () =>
      getColorSchemesList({
        searchText: searchValue,
        page: page + 1,
        perPage,
        isActive: showInactive ? null : true
      }),
    {
      onSuccess: (d: any) => {
        const data: IResponse<any[]> = d;
        setRecordCount(data.totalRecordCount);
        return d;
      },
      onError: (e: any) => {
        enqueueSnackbar(e?.Detail || `Error loading color schemes.`, {
          variant: 'error'
        });
      },
      notifyOnChangeProps: 'tracked'
    }
  );

  return (
    <>
      <Page title="Manage Color Schemes">
        <PageTitleWithButton
          pageTitleProps={{
            title: 'Manage Color Schemes'
          }}
          button={
            <Box display="flex">
              <FormControlLabel
                className="print--none"
                control={
                  <Switch
                    color="primary"
                    checked={showInactive}
                    id="active-inactive"
                    disabled={isLoadingColorSchemes}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setShowInactive(e.target.checked);
                    }}
                  />
                }
                label="Show Inactive"
              />
              <Divider orientation="vertical" flexItem sx={{ margin: '0 1rem 0 0' }} />
              <Link
                className={classes.link}
                to={`/development/color-schemes/add/general-information`}
              >
                <Button
                  startIcon={<Add />}
                  color="primary"
                  variant="contained"
                  className={classes.addColorSchemeButton}
                >
                  Add Color Scheme
                </Button>
              </Link>
            </Box>
          }
        />
        <Box>
          <ManageColorSchemeFilter
            isLoading={isLoadingColorSchemes}
            setHasAppliedFilters={setHasAppliedFilters}
            hasAppliedFilters={hasAppliedFilters}
            setSearchValue={setSearch}
            searchValue={searchValue}
            handleSearch={(val: string) => {
              setFilters({
                ...filters,
                search: val
              });
            }}
            applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
              if (clearFilters) {
                setFilters({
                  search: ''
                });
              } else {
                setFilters({
                  ...filters,
                  search: searchValue
                });
              }
            }}
          />
          {isLoadingColorSchemes && (
            <Box className={classes.loader}>
              <Loader position="centered" type="inline" />
            </Box>
          )}
          {!isLoadingColorSchemes && colorSchemes?.records?.length === 0 && (
            <Box display="flex" justifyContent="center" marginTop="1rem">
              <Typography variant="body1" pt={8} gutterBottom>
                No color schemes found.
              </Typography>
            </Box>
          )}
          {!isLoadingColorSchemes && colorSchemes?.records?.length > 0 && (
            <Box display="flex" justifyContent="space-between">
              <div className={classes.boldName}>
                <span className={classes.panelItem}>Code</span>
                <span className={classes.panelItem}>Price</span>
                <span className={classes.panelItem}>Status</span>
              </div>
              <div>
                <Tooltip
                  title={`${isAccordionsExpanded ? 'Collapse' : 'Expand'} all color schemes`}
                >
                  <IconButton onClick={() => setAccordionsExpanded(!isAccordionsExpanded)}>
                    {isAccordionsExpanded ? <UnfoldLess /> : <UnfoldMore />}
                  </IconButton>
                </Tooltip>
              </div>
            </Box>
          )}
          {!isLoadingColorSchemes &&
            colorSchemes?.records.map((item, index) => {
              return (
                <Fragment key={`${index}`}>
                  <ColorSchemeAccordion
                    colorSchemeItem={item}
                    refetchData={refetchColorSchemeList}
                    isAllAccordionsExpanded={isAccordionsExpanded}
                  />
                </Fragment>
              );
            })}
          {!isLoadingColorSchemes && colorSchemes?.records.length > 0 && (
            <Box
              sx={{
                backgroundColor: theme => theme.palette.grey[200],
                display: 'flex',
                justifyContent: 'flex-end'
              }}
            >
              <Pagination
                page={page}
                count={recordCount}
                rowsPerPage={perPage}
                setPage={setPage}
                setRowsPerPage={setRowsPerPage}
                labelRowsPerPage="Rows per page:"
              />
            </Box>
          )}
        </Box>
      </Page>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  list: {
    maxHeight: `30rem`,
    overflow: 'auto',
    margin: theme.spacing(1, 0)
  },
  addColorSchemeButton: {
    marginTop: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
      width: 'auto'
    }
  },
  boldName: {
    paddingLeft: '16px',
    marginBottom: '-10px',
    fontWeight: 600,
    display: 'grid',
    gridTemplateColumns: '100px 150px 100px',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '65px 75px 50px',
      paddingLeft: '5px'
    }
  },
  panelItem: {
    flex: 1
  },
  loader: {
    paddingTop: theme.spacing(8)
  },
  link: {
    textDecoration: 'none'
  }
}));
